import { css, html, LitElement, TemplateResult } from "lit";
import {property, customElement} from 'lit/decorators.js';
import { classMap } from 'lit-html/directives/class-map';
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { VoyageShoreContextModel } from "../../../src/models/voyage-shore-context-model";
import { ShoreContextEvent } from "../../../src/models/types/voyage-shore-context-model.types";

export interface TypeAHeadOption {
    label: string;
    value: string;
};

export interface TypeAHeadSelectEvent extends TypeAHeadOption {
    index: number
}

@customElement('voy-input-type-a-head')
export class VoyInputTypeAHead extends LitElement {
    @property({ type: String }) public placeholder: string = 'Search';
    @property({ type: String }) public errorMessage: string = 'Try to relax the search parameters.';
    @property({ type: Array, attribute: false }) public options: Array<TypeAHeadOption> = [];
    @property({ type: Boolean, attribute: false }) public showLabel: boolean = false;
    @property({ type: Number, attribute: false }) private _listLength: number = 0;
    @property({ type: Number, attribute: false }) private _shownOptions: string[] = [];
    @property({ type: Number, attribute: false }) private _validInputItems: string[] = [];
    @property({ type: Boolean, attribute: false }) private _inputExists: boolean = true;
    @property({ type: Boolean, attribute: false }) private _showOptions: boolean = false;
    @property({ attribute: false }) private _search: string = "";
    @property({ type: Boolean, reflect: true }) public disabled: boolean = false;

    private _input: HTMLInputElement | null | undefined;

    constructor() {
        super();

        // this resets the input field on click and re-renders the dropdown
        this.addEventListener('focus', () => {
            this.reset();
        });

        this._handleChange = this._handleChange.bind(this);
        this._onSelect = this._onSelect.bind(this);
        this._toggleShowOptions = this._toggleShowOptions.bind(this);
        this.reset = this.reset.bind(this);
       
    }

    get listLength() {
        return this._listLength;
    }

    get shownOptions() {
        return this._shownOptions;
    }

    get validInputItems() {
        return this._validInputItems;
    }
    
    static get styles() {
        return css`
            :host {
                display: block;
                position: relative;
                font-family: MaerskText;
                font-size: 14px;
                line-height: 16px;
                font-weight: 400;
            }
            
            input {
                width: 100%;
                padding: 12px 10px 12px 40px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 5px;
                font-size: 14px;
            }
            input:focus,
            input:active {
                outline: none;
                box-shadow: none;
            }

            input::placeholder {
                color: var(--neutrals-grey-05, grey);
            }

            input:disabled {
                background-color: var(--neutrals-grey-08, grey);
                color: var(--neutrals-grey-04, grey);
                cursor: not-allowed;
            }

            .options {
                margin: 0;
                padding: 0;
                box-shadow: 1px 3px 10px 0px rgba(0,0,0,.2);
                background-color: var(--neutrals-grey-10, grey);
                border-radius: 5px;
                max-height: 130px;
                overflow-y: auto;
                position: absolute;
                visibility: hidden;
                width: 100%;
                z-index: 1;
            }
            .options[show] {
                visibility: visible;
                overflow-y: auto;
            }
            .option {
                list-style: none;
                cursor: pointer;
                pointer-events: all;
                font-size: 14px;
                color: var(--neutrals-grey-02, grey);
                padding: 10px 12px;
                pointer-events: all;
            }
            .option:hover {
                background-color: var(--neutrals-grey-08, grey);
            }

            .option.no-results h2,
            .option.no-results p {
                font-size: 14px;
                line-height: 20px;
                margin: 0;
            }

            .results {
                padding-left: 16px;
            }
            
            input.invalid {
                color: red;
            }
            svg {
                position: absolute;
                left: 10px;
                top: 10px;
            }
            .disabled {
                pointer-events: none;
                opacity: 0.5;
            }
        `
    }

    reset(): void {
        this._search = '';
        this._input!.value = '';
        this.requestUpdate();
    }

    private _handleChange(): void {
        if (this.disabled) return; // Disabling functionality if the component is disabled

        const inputValues = this._input!.value.split(/[,;]/).map(value => value.trim());
        this._search = inputValues[inputValues.length - 1];
      
        const shownOptions: string[] = [];
        //setting to true by default
        let inputExists = true; 

        // veryfing that the input values exist
          for (let inputValue of inputValues) {
            let exists = false;
            for (let option of this.options) {
              if (option.label.toLowerCase().includes(inputValue.toLowerCase()) || String(option.value).includes(String(inputValue))) {
                shownOptions.push(option.value);
                exists = true;
              }
            }
            if (!exists) {
              inputExists = false;
              break;
            }
          }
          
        this._validInputItems = inputValues;
        this._shownOptions = shownOptions;
        this._inputExists = inputExists;
    
        // adding 'invalid' CSS class to change color to red
        if (inputExists) {
          this._input!.classList.remove('invalid');
        } else {
          this._input!.classList.add('invalid');
        }
      }
      
    private _onSelect(e: Event): void {
        if (this.disabled) return;

        const option = e.currentTarget as HTMLLIElement;
        const value = option.getAttribute("data-value")!;
        const label = option.getAttribute("data-label")!;
        const index = parseInt(option.getAttribute("data-index")!, 10);
    
        const selectedOption: TypeAHeadOption = { value, label };
    
        // This displays the dropdown selection in the input field
        this.reset();
        this._input!.value = `${this._input!.value}${label}`;
    
        const detail: TypeAHeadSelectEvent = { ...selectedOption, index   };

        this.dispatchEvent(new CustomEvent("change", { detail }));
        this._showOptions = true;
    }
    
    private _toggleShowOptions(wait: boolean): void {
        if (this.disabled) return;

        if (wait) {
            setTimeout(() => { this._showOptions = !this._showOptions }, 150)
        } else {
            this._showOptions = !this._showOptions;
        }
    }
    
    private _handleKeyDown(event: KeyboardEvent) {
        if (this.disabled) return;
        if (event.key === "Enter") {
          
          this.reset();
        }
      }

    private _renderOptions(): TemplateResult | TemplateResult[] {
        const options: TemplateResult[] = []
        const shownOptions: string[] = [];
               
        for (let i = 0; i < this.options.length; i++) {
            const opt = this.options[i];
            if (!(opt.label.toLowerCase().includes(this._search.toLowerCase()) || String(opt.value).includes(String(this._search)))) {
                continue
            }

            options.push(html`
                <li class="option" 
                    data-value=${opt.value}
                    data-label=${opt.label}
                    data-index=${i} 
                    @click=${this._onSelect}>
                    ${this.renderItem(opt)}
                </li>
            `)
            shownOptions.push(opt.value);
        }
        
        this._listLength = options.length;
        this._shownOptions = shownOptions;

        if (!options.length) {
            return html`<p class="results">No results</p>`
        }

        return options
    }
    
    firstUpdated() {
        const input = this.shadowRoot?.querySelector("input");
        this._input = input;
    }

    render() {
        return html`
            ${SvgIcons.customSearch}
            <input 
                type="text"
                placeholder=${this.placeholder} 
                @input=${this._handleChange}
                @focus=${() => this._toggleShowOptions(false)}
                @blur=${() => this._toggleShowOptions(true)}
                @keydown=${this._handleKeyDown}
                class="${classMap({ 'invalid': !this._inputExists })}" 
                ?disabled=${this.disabled}/>
            <ul class="options" ?show=${this._showOptions}>
                ${this._renderOptions()}
            </ul>
        `
    }

    connectedCallback() {
        super.connectedCallback();
        VoyageShoreContextModel!.addEventListener(ShoreContextEvent.ClearFilters, this.reset);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        VoyageShoreContextModel!.removeEventListener(ShoreContextEvent.ClearFilters, this.reset);
        
    }

    public renderItem(item: TypeAHeadOption): TemplateResult {
        return html`${this.showLabel ? `${item.label} (${item.value})`: item.label}`;
    }
}
